import { resolveUploadPortPath } from '@/features/upload/resolveUploadPort';
import { detectRequiredLibraries } from '@/features/upload/detectRequiredLibraries';
import { formatUploadSummary } from '@/features/upload/formatUploadSummary';
import { getHelperConnectionState, isHelperAvailable, uploadSketch } from '@/features/upload/uploadService';
import { releaseSerialForUpload } from '@/features/serial/serialManager';
import { fetchHelperHealth, fetchMissingLibraries } from '@/features/serial/helperClient';
import {
  HELPER_LIBRARY_AUTOINSTALL_VERSION,
  helperSupportsLibraryAutoinstall,
} from '@/features/helper/helperVersion';
import { getHelperDownloadUrl } from '@/config/helperDownload';
import { useBoardStore } from '@/state/boardStore';
import { useConsoleStore } from '@/state/consoleStore';
import { showHelperOfflineDialog } from '@/features/helper/helperOfflineDialog';
import { useArduinoSetupDialogStore } from '@/state/arduinoSetupDialogStore';
import { useSerialStore } from '@/state/serialStore';
import { useUploadResultDialogStore } from '@/state/uploadResultDialogStore';
import { useWorkspaceStore } from '@/state/workspaceStore';
import { create } from 'zustand';

interface UploadState {
  uploading: boolean;
  uploadProgress: number;
  uploadStatus: string;
  upload: () => Promise<void>;
}

const UPLOAD_PORT_RELEASE_MS = 1500;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function summarizeUploadError(message: string): string {
  const lower = message.toLowerCase();
  if (
    lower.includes('no such file') &&
    (lower.includes('.h') || lower.includes('directory'))
  ) {
    return `${message}\n\nTip: This sketch needs an Arduino library. Install Skyrover Helper v${HELPER_LIBRARY_AUTOINSTALL_VERSION} or newer so libraries are installed automatically before compile.`;
  }
  if (
    lower.includes('access is denied') ||
    lower.includes('could not open') ||
    lower.includes('busy') ||
    lower.includes('permission denied')
  ) {
    return `${message}\n\nTip: Close the Serial tab, wait a moment, and try Upload again. Only one app can use the COM port at a time.`;
  }
  if (lower.includes('timed out waiting for packet header') || lower.includes('failed to connect to esp')) {
    return `${message}\n\nTip: For ESP boards, hold the BOOT button while uploading, or check that the correct board is selected.`;
  }
  return message;
}

export const useUploadStore = create<UploadState>((set, get) => ({
  uploading: false,
  uploadProgress: 0,
  uploadStatus: '',
  upload: async () => {
    if (get().uploading) {
      return;
    }

    const code = useWorkspaceStore.getState().generatedCode.trim();
    const board = useBoardStore.getState().board;
    const serial = useSerialStore.getState();
    const addMessage = useConsoleStore.getState().addMessage;

    if (!code) {
      addMessage('error', 'Nothing to upload. Add blocks to your workspace first.');
      return;
    }

    if (!serial.connected) {
      addMessage('error', 'Connect to the board before uploading.');
      return;
    }

    const helperState = await getHelperConnectionState();
    if (helperState === 'offline') {
      showHelperOfflineDialog();
      addMessage('error', 'Skyrover Helper is not running on this PC. Start or install the helper to upload.');
      return;
    }
    if (helperState === 'setup_required') {
      useArduinoSetupDialogStore.getState().show();
      addMessage('error', 'Arduino CLI is not installed yet. Use Install CLI, then download your board from Board Manager.');
      return;
    }

    if (!(await isHelperAvailable())) {
      return;
    }

    const portPath = resolveUploadPortPath(serial.port, serial.availablePorts);
    if (!portPath) {
      addMessage(
        'error',
        'Select a COM port before uploading. Refresh the port list after plugging in your board.',
      );
      return;
    }

    const requiredLibraries = detectRequiredLibraries(code);
    const helperHealth = await fetchHelperHealth();
    const canAutoInstallLibraries = helperSupportsLibraryAutoinstall(helperHealth?.version);

    if (requiredLibraries.length > 0 && !canAutoInstallLibraries) {
      const libList = requiredLibraries.join(', ');
      const helperVersion = helperHealth?.version ?? 'unknown';
      const message =
        `This sketch needs Arduino libraries (${libList}), but Skyrover Helper v${helperVersion} cannot install them automatically.\n\n` +
        `Install Skyrover Helper v${HELPER_LIBRARY_AUTOINSTALL_VERSION} or newer, then try Upload again:\n${getHelperDownloadUrl()}`;
      addMessage('error', message);
      useUploadResultDialogStore.getState().showError(message);
      return;
    }

    const librariesForUpload = canAutoInstallLibraries ? requiredLibraries : [];
    let librariesToInstall = librariesForUpload;
    const wasConnected = serial.connected;

    if (librariesForUpload.length > 0) {
      try {
        librariesToInstall = await fetchMissingLibraries(librariesForUpload);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Could not check installed libraries.';
        addMessage('error', message);
        useUploadResultDialogStore.getState().showError(message);
        return;
      }
    }

    set({
      uploading: true,
      uploadProgress: 8,
      uploadStatus: 'Preparing upload…',
    });
    addMessage('info', `Uploading to ${portPath} (${board.displayName})…`);

    if (wasConnected) {
      set({ uploadProgress: 12, uploadStatus: 'Releasing COM port…' });
      await useSerialStore.getState().disconnect({ forUpload: true });
      await releaseSerialForUpload();
      await sleep(UPLOAD_PORT_RELEASE_MS);
    }

    set({
      uploadProgress: 10,
      uploadStatus: 'Starting compile…',
    });

    if (librariesToInstall.length > 0) {
      addMessage(
        'info',
        `Installing required libraries: ${librariesToInstall.join(', ')}. This may take a moment on first upload.`,
      );
      set({
        uploadProgress: 18,
        uploadStatus: 'Installing required libraries…',
      });
    }

    try {
      const result = await uploadSketch({
        code,
        fqbn: board.fqbn,
        port: portPath,
        librariesToInstall,
        onProgress: (progress, status) => {
          set({ uploadProgress: progress, uploadStatus: status });
        },
      });

      const summary = formatUploadSummary(result.log);
      addMessage('success', summary ?? 'Upload complete.');
      useUploadResultDialogStore.getState().showSuccess(board.displayName);

      if (wasConnected) {
        set({ uploadProgress: 95, uploadStatus: 'Reconnecting serial monitor…' });
        await useSerialStore.getState().connect();
      }

      set({ uploadProgress: 100, uploadStatus: 'Done' });
    } catch (error) {
      const message = summarizeUploadError(
        error instanceof Error ? error.message : 'Upload failed.',
      );
      addMessage('error', message);
      useUploadResultDialogStore.getState().showError(message);

      if (wasConnected && !useSerialStore.getState().connected) {
        set({ uploadProgress: 95, uploadStatus: 'Reconnecting serial monitor…' });
        try {
          await useSerialStore.getState().connect();
        } catch {
          addMessage('warning', 'Upload finished but serial monitor could not reconnect.');
          useSerialStore.setState({ pausedForUpload: false });
        }
      }
    } finally {
      set({ uploading: false, uploadProgress: 0, uploadStatus: '' });
      if (useSerialStore.getState().pausedForUpload && !useSerialStore.getState().connected) {
        useSerialStore.setState({ pausedForUpload: false });
      }
    }
  },
}));
